import React from 'react';

import { useQueryParams } from './useQueryParams';
import usePageNav from './usePageNav';
import { compareFilters, removeEmptyFilters } from './utils';
import usePerPageCount from './usePerPageCount';

const useQueryFilters = (defaultPerPage = 20, possiblePerPageCount = []) => {
    const [queryCache, setQueryCache] = React.useState({});
    const [params, setParams] = useQueryParams({}, true);
    const [initialized, setInitialized] = React.useState(false);
    React.useEffect(() => {
        if (Object.keys(params).length === 0) {
            setParams({ page: '0' }, { replace: true });
        }
        setInitialized(true);
    }, [params, setParams]);

    const [currentPage, setCurrentPage] = usePageNav(params, newPage => {
        setParams({
            ...queryCache,
            page: newPage.toString()
        });
    });

    const [perPageCount, setPerPageCount] = usePerPageCount(params, defaultPerPage, possiblePerPageCount, newPerPage => {
        setParams({
            ...queryCache,
            page: '0',
            per_page: newPerPage.toString()
        });
    });

    const filters = React.useMemo(() => {
        // eslint-disable-next-line no-unused-vars
        const { search, ordering, page, ...oldFilters } = queryCache;
        return { ...oldFilters };
    }, [queryCache]);

    const setFilters = React.useCallback(
        newFilters => {
            const preparedFilters = removeEmptyFilters(newFilters);
            setParams({
                search: queryCache.search,
                ordering: queryCache.ordering,
                page: currentPage.toString(),
                ...preparedFilters
            });
        },
        [currentPage, queryCache.ordering, queryCache.search, setParams]
    );
    React.useEffect(() => {
        if (!initialized && !Object.keys(params).length) {
            return;
        }
        setQueryCache(params);
    }, [params, initialized]);

    const offset = React.useMemo(() => {
        return currentPage * perPageCount;
    }, [currentPage, perPageCount]);

    return React.useMemo(() => {
        return {
            initialized: initialized,
            appliedFilters: queryCache,
            filters: filters,
            ordering: queryCache.ordering,
            search: queryCache.search,
            setFilters: newSearchParams => {
                if (!compareFilters(filters, newSearchParams)) {
                    setFilters({ ...newSearchParams, page: '0' });
                }
            },
            setSearch: newSearch => {
                const newParams = { ...params };
                if (newSearch) {
                    newParams.search = newSearch;
                } else {
                    delete newParams.search;
                }
                setParams({ ...newParams });
            },
            setOrdering: newOrdering => {
                setParams({ ...params, ordering: newOrdering });
            },
            currentPage,
            setCurrentPage,
            offset,
            perPageCount: perPageCount,
            setPerPageCount: setPerPageCount
        };
    }, [queryCache, currentPage, filters, initialized, params, perPageCount, setCurrentPage, setFilters, setParams, setPerPageCount]);
};

export default useQueryFilters;
